import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, X, Pencil, Trash2, EyeOff, Eye } from 'lucide-react';
import { type ColumnDef, type SortingState, type ColumnSizingState } from '@tanstack/react-table';
import { toast } from '../../hooks/useToast';
import { getErrorMessage } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { manufacturerService } from '../../services/manufacturer.service';
import { distributorService } from '../../services/distributor.service';
import type { Manufacturer, Distributor, PaginationMeta } from '../../types';
import { DataTable } from '../../components/DataTable';
import { AuditMetadata } from '../../components/AuditMetadata';
import { MultiSelect, type MultiSelectOption } from '../../components/MultiSelect';

// Sentinel sent for the (Blank) zip filter option. Backend strips it before
// passing the rest to ransack and layers a blank-OR scope on top.
const BLANK_SENTINEL = '__BLANK__';

type Tab = 'manufacturers' | 'distributors';

const manufacturerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
});
type ManufacturerFormData = z.infer<typeof manufacturerSchema>;

const distributorSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    code: z.string().optional(),
    region: z.string().optional(),
    zip_code: z.string().optional(),
  })
  .refine(
    (data) => !(data.region?.trim().toUpperCase() === 'OR' && !data.zip_code?.trim()),
    { message: 'Zip is required when Region is OR', path: ['zip_code'] }
  );
type DistributorFormData = z.infer<typeof distributorSchema>;

export const Partners = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as Tab) || 'manufacturers';
  const setTab = (next: Tab) => setSearchParams({ tab: next });

  const isAdmin = useAuthStore((state) => state.user?.role === 'account_admin');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Partners</h1>
        <p className="text-sm text-gray-600 mt-1">Manage manufacturers and distributors</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {(['manufacturers', 'distributors'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-semibold capitalize ${
                tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'manufacturers' ? <ManufacturersTab isAdmin={isAdmin} /> : <DistributorsTab isAdmin={isAdmin} />}
    </div>
  );
};

// ----- Manufacturers tab -----

const ManufacturersTab = ({ isAdmin }: { isAdmin: boolean }) => {
  const [items, setItems] = useState<Manufacturer[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Manufacturer | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ManufacturerFormData>({
    resolver: zodResolver(manufacturerSchema),
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await manufacturerService.list({
        page,
        per_page: 25,
        q: { name_cont: search || undefined },
        sort: sorting,
      });
      setItems(result.data);
      setMeta(result.meta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, search, sorting]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); reset({ name: '' }); setShowModal(true); };
  const openEdit = (m: Manufacturer) => { setEditing(m); reset({ name: m.name }); setShowModal(true); };

  const onSubmit = async (data: ManufacturerFormData) => {
    setIsSaving(true);
    try {
      if (editing) {
        await manufacturerService.update(editing.id, data);
        toast.success('Manufacturer updated');
      } else {
        await manufacturerService.create(data);
        toast.success('Manufacturer created');
      }
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete manufacturer "${editing.name}"?`)) return;
    try {
      await manufacturerService.destroy(editing.id);
      toast.success('Manufacturer deleted');
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async () => {
    if (!editing) return;
    const verb = editing.active ? 'Disable' : 'Enable';
    if (!window.confirm(`${verb} manufacturer "${editing.name}"?`)) return;
    try {
      const updated = await manufacturerService.toggleActive(editing.id);
      toast.success(`Manufacturer ${updated.active ? 'enabled' : 'disabled'}`);
      setEditing(updated);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const columns = useMemo<ColumnDef<Manufacturer, any>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2">
            <span className={row.original.active ? 'font-medium' : 'font-medium text-gray-500'}>
              {row.original.name}
            </span>
            {!row.original.active && (
              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                Inactive
              </span>
            )}
          </span>
        ),
        size: 400,
      },
      ...(isAdmin
        ? [{
            id: 'actions',
            header: '',
            enableSorting: false,
            enableResizing: false,
            size: 80,
            cell: ({ row }) => (
              <div className="text-right">
                <button onClick={() => openEdit(row.original)} className="text-blue-600 hover:text-blue-900">
                  <Pencil className="h-4 w-4 inline" />
                </button>
              </div>
            ),
          } as ColumnDef<Manufacturer, any>]
        : []),
    ],
    [isAdmin]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          className="input flex-1 max-w-sm"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {isAdmin && (
          <button onClick={openCreate} className="btn btn-primary"><Plus className="h-4 w-4 inline mr-2" />Add Manufacturer</button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={items}
        meta={meta}
        isLoading={isLoading}
        page={page}
        onPageChange={setPage}
        sorting={sorting}
        onSortingChange={setSorting}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage="No manufacturers"
      />

      {showModal && (
        <ModalShell title={editing ? 'Edit Manufacturer' : 'Add Manufacturer'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 pt-5 pb-4 space-y-4">
              <div>
                <label className="label">Name</label>
                <input {...register('name')} className="input" />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              {editing && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-semibold">{editing.commission_entries_count}</span>
                  <span>commission {editing.commission_entries_count === 1 ? 'entry is' : 'entries are'} linked to this manufacturer.</span>
                  {!editing.active && (
                    <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                      Disabled
                    </span>
                  )}
                </div>
              )}
              {editing && <AuditMetadata record={editing} />}
            </div>
            <ModalFooter
              onCancel={() => setShowModal(false)}
              isSaving={isSaving}
              editing={!!editing}
              destructiveAction={destructiveActionFor(editing, handleDelete, handleToggleActive)}
              destructiveHelp={destructiveHelpFor(editing)}
            />
          </form>
        </ModalShell>
      )}
    </div>
  );
};

// ----- Distributors tab -----

const DistributorsTab = ({ isAdmin }: { isAdmin: boolean }) => {
  const [items, setItems] = useState<Distributor[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Distributor | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [regionOptions, setRegionOptions] = useState<string[]>([]);
  const [zipOptions, setZipOptions] = useState<string[]>([]);
  const [regionFilter, setRegionFilter] = useState<string[]>([]);
  const [zipFilter, setZipFilter] = useState<string[]>([]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<DistributorFormData>({
    resolver: zodResolver(distributorSchema),
  });

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await distributorService.list({
        page,
        per_page: 25,
        q: {
          name_cont: search || undefined,
          region_in: regionFilter,
          zip_code_in: zipFilter,
        },
        sort: sorting,
      });
      setItems(result.data);
      setMeta(result.meta);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [page, search, sorting, regionFilter, zipFilter]);

  useEffect(() => { load(); }, [load]);

  // Load distinct region/zip values once on mount. Refresh after each save
  // so newly created values appear in the filter and form datalists.
  const loadFilterOptions = useCallback(() => {
    distributorService
      .filterOptions()
      .then((opts) => {
        setRegionOptions(opts.regions);
        setZipOptions(opts.zip_codes);
      })
      .catch((error) => toast.error(getErrorMessage(error)));
  }, []);

  useEffect(() => { loadFilterOptions(); }, [loadFilterOptions]);

  const openCreate = () => { setEditing(null); reset({ name: '', code: '', region: '', zip_code: '' }); setShowModal(true); };
  const openEdit = (d: Distributor) => {
    setEditing(d);
    reset({ name: d.name, code: d.code ?? '', region: d.region ?? '', zip_code: d.zip_code ?? '' });
    setShowModal(true);
  };

  const onSubmit = async (data: DistributorFormData) => {
    setIsSaving(true);
    try {
      if (editing) {
        await distributorService.update(editing.id, data);
        toast.success('Distributor updated');
      } else {
        await distributorService.create(data);
        toast.success('Distributor created');
      }
      setShowModal(false);
      load();
      // Pick up any newly-introduced region/zip in the dropdowns
      loadFilterOptions();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete distributor "${editing.name}"?`)) return;
    try {
      await distributorService.destroy(editing.id);
      toast.success('Distributor deleted');
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async () => {
    if (!editing) return;
    const verb = editing.active ? 'Disable' : 'Enable';
    if (!window.confirm(`${verb} distributor "${editing.name}"?`)) return;
    try {
      const updated = await distributorService.toggleActive(editing.id);
      toast.success(`Distributor ${updated.active ? 'enabled' : 'disabled'}`);
      setEditing(updated);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const columns = useMemo<ColumnDef<Distributor, any>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2">
            <span className={row.original.active ? 'font-medium' : 'font-medium text-gray-500'}>
              {row.original.name}
            </span>
            {!row.original.active && (
              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                Inactive
              </span>
            )}
          </span>
        ),
        size: 280,
      },
      {
        id: 'code',
        header: 'Code',
        accessorKey: 'code',
        cell: (info) => (info.getValue() as string | null) ?? '—',
        size: 140,
      },
      {
        id: 'region',
        header: 'Region',
        accessorKey: 'region',
        cell: (info) => (info.getValue() as string | null) ?? '—',
        size: 160,
      },
      {
        id: 'zip_code',
        header: 'Zip',
        accessorKey: 'zip_code',
        cell: (info) => (info.getValue() as string | null) ?? '—',
        size: 120,
      },
      ...(isAdmin
        ? [{
            id: 'actions',
            header: '',
            enableSorting: false,
            enableResizing: false,
            size: 80,
            cell: ({ row }) => (
              <div className="text-right">
                <button onClick={() => openEdit(row.original)} className="text-blue-600 hover:text-blue-900">
                  <Pencil className="h-4 w-4 inline" />
                </button>
              </div>
            ),
          } as ColumnDef<Distributor, any>]
        : []),
    ],
    [isAdmin]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <input
          className="input flex-1 max-w-sm"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {isAdmin && (
          <button onClick={openCreate} className="btn btn-primary"><Plus className="h-4 w-4 inline mr-2" />Add Distributor</button>
        )}
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Region</label>
            <MultiSelect
              value={regionFilter}
              onChange={(v) => { setRegionFilter(v); setPage(1); }}
              options={regionOptions.map<MultiSelectOption>((r) => ({ value: r, label: r }))}
            />
          </div>
          <div>
            <label className="label">Zip</label>
            <MultiSelect
              value={zipFilter}
              onChange={(v) => { setZipFilter(v); setPage(1); }}
              options={[
                { value: BLANK_SENTINEL, label: '(Blank)' },
                ...zipOptions.map<MultiSelectOption>((z) => ({ value: z, label: z })),
              ]}
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => { setRegionFilter([]); setZipFilter([]); setPage(1); }}
              className="btn btn-secondary w-full"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        meta={meta}
        isLoading={isLoading}
        page={page}
        onPageChange={setPage}
        sorting={sorting}
        onSortingChange={setSorting}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage="No distributors"
      />

      {showModal && (
        <ModalShell title={editing ? 'Edit Distributor' : 'Add Distributor'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="px-6 pt-5 pb-4 space-y-4">
              <div>
                <label className="label">Name</label>
                <input {...register('name')} className="input" />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label className="label">Code</label>
                <input {...register('code')} className="input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Region</label>
                  <input
                    {...register('region')}
                    list="distributor-region-options"
                    autoComplete="off"
                    placeholder="Pick or type a value"
                    className="input"
                  />
                  <datalist id="distributor-region-options">
                    {regionOptions.map((r) => (
                      <option key={r} value={r} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="label">Zip</label>
                  <input {...register('zip_code')} className="input" />
                  {errors.zip_code && <p className="mt-1 text-sm text-red-600">{errors.zip_code.message}</p>}
                </div>
              </div>
              {editing && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-semibold">{editing.commission_entries_count}</span>
                  <span>commission {editing.commission_entries_count === 1 ? 'entry is' : 'entries are'} linked to this distributor.</span>
                  {!editing.active && (
                    <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-200 text-gray-700">
                      Disabled
                    </span>
                  )}
                </div>
              )}
              {editing && <AuditMetadata record={editing} />}
            </div>
            <ModalFooter
              onCancel={() => setShowModal(false)}
              isSaving={isSaving}
              editing={!!editing}
              destructiveAction={destructiveActionFor(editing, handleDelete, handleToggleActive)}
              destructiveHelp={destructiveHelpFor(editing)}
            />
          </form>
        </ModalShell>
      )}
    </div>
  );
};

// ----- Shared modal pieces -----

interface ToggleableRecord {
  active: boolean;
  commission_entries_count: number;
}

/**
 * Decides which destructive action to surface in the edit modal:
 *  - 0 linked entries → Delete (red)
 *  - active + ≥1 linked entries → Disable (red)
 *  - inactive → Enable (green)
 *
 * Returning undefined hides the destructive action entirely (used when
 * we're not editing an existing record).
 */
function destructiveActionFor(
  record: ToggleableRecord | null,
  onDelete: () => void,
  onToggleActive: () => void
): DestructiveAction | undefined {
  if (!record) return undefined;
  if (record.commission_entries_count === 0) {
    return { label: 'Delete', icon: Trash2, onClick: onDelete };
  }
  if (record.active) {
    return { label: 'Disable', icon: EyeOff, onClick: onToggleActive };
  }
  return {
    label: 'Enable',
    icon: Eye,
    onClick: onToggleActive,
    colorClass: 'text-green-700 hover:text-green-800',
  };
}

/** Help text shown above the modal footer explaining the available action. */
function destructiveHelpFor(record: ToggleableRecord | null): string | undefined {
  if (!record) return undefined;
  if (record.commission_entries_count === 0) {
    return 'No commission entries are linked. Delete will permanently remove this partner.';
  }
  if (record.active) {
    return 'Delete is not available because commission entries are linked to this partner. Disable will hide it from new commission entry forms while keeping the existing entries and history intact.';
  }
  return 'This partner is currently disabled and won’t appear on new commission entry forms. Enable to make it selectable again.';
}


const ModalShell = ({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    <div className="flex items-center justify-center min-h-screen px-4 py-8">
      <div className="fixed inset-0 bg-(--color-bg-scrim)" onClick={onClose} />
      <div className="relative inline-block bg-white rounded-lg text-left shadow-xl sm:max-w-lg sm:w-full">
        <div className="px-6 pt-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500"><X className="h-6 w-6" /></button>
          </div>
        </div>
        {children}
      </div>
    </div>
  </div>
);

interface DestructiveAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  /** Tailwind text color class. Defaults to red. */
  colorClass?: string;
}

interface ModalFooterProps {
  onCancel: () => void;
  isSaving: boolean;
  editing: boolean;
  destructiveAction?: DestructiveAction;
  /** Optional help text shown above the footer explaining the destructive action. */
  destructiveHelp?: string;
}

const ModalFooter = ({ onCancel, isSaving, editing, destructiveAction, destructiveHelp }: ModalFooterProps) => (
  <>
    {destructiveHelp && (
      <div className="bg-amber-50 border-t border-amber-200 px-6 py-3 text-xs text-amber-800">
        {destructiveHelp}
      </div>
    )}
    <div className="bg-gray-50 px-6 py-3 flex items-center gap-3 rounded-b-lg">
      {destructiveAction && (
        <button
          type="button"
          onClick={destructiveAction.onClick}
          className={`${destructiveAction.colorClass ?? 'text-red-600 hover:text-red-700'} text-sm font-semibold inline-flex items-center gap-1`}
        >
          <destructiveAction.icon className="h-4 w-4" />
          {destructiveAction.label}
        </button>
      )}
      <div className="ml-auto flex gap-3">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
        <button type="submit" disabled={isSaving} className="btn btn-primary">
          {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
        </button>
      </div>
    </div>
  </>
);

export default Partners;
